import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Card from '../../components/Card';
import { Tabs, Row, Col, Spin, Empty, Pagination, Popover } from 'antd';
import { QuestionCircleTwoTone } from '@ant-design/icons';
import { CRS_CLUSTER_RISK_INDICATOR_TAB } from '../../../constants/crsContants';
import { getInDecimal } from '../../../../services/util';
import style from './index.scss';

const { TabPane } = Tabs;

const ClusterRiskIndicators = ({
  activeTabKey,
  tabChange,
  isLoading,
  latestCluster,
  clusterInformation,
  onPaginationChange,
  defaultPage,
  defaultPageSize,
  currentPage
}) => {
  const [quadrant, setQuadrant] = useState({});
  const [riskIndicators, setRiskIndicators] = useState([]);
  const [showFullValue, setShowFullValue] = useState('');

  useEffect(() => {
    const { customerRiskScore = 'NA', clusterMeanScore = 'NA' } = latestCluster;
    let riskScore = parseFloat(customerRiskScore);
    let meanScore = parseFloat(clusterMeanScore.split('%')[0]);
    let quadrantName = '';

    // Q1
    if (riskScore >= 50 && meanScore >= 50) {
      quadrantName = 'Q1';
    }
    // Q2
    else if (riskScore <= 50 && meanScore >= 50) {
      quadrantName = 'Q2';
    }
    // Q3
    else if (riskScore <= 50 && meanScore <= 50) {
      quadrantName = 'Q3';
    }
    // Q4
    else if (riskScore >= 50 && meanScore <= 50) {
      quadrantName = 'Q4';
    }

    setQuadrant({
      quadrantName,
      riskScore: customerRiskScore,
      meanScore: clusterMeanScore.split('%')[0]
    });
  }, [latestCluster]);

  useEffect(() => {
    setRiskIndicators(clusterInformation?.clusterRiskIndicatorList || []);
  }, [clusterInformation]);

  const getTabValue = (tabName) => {
    switch (tabName) {
      // case 'CUSTOMER':
      //   return 'Customer';
      // case 'TRANSACTIONS':
      //   return 'Transactions';
      case 'HISTORICAL RISK':
        return 'HISTORICAL_RISK';
      default:
        return tabName;
    }
  };

  const onTabChange = (key) => {
    tabChange(getTabValue(CRS_CLUSTER_RISK_INDICATOR_TAB[key - 1]), key);
  };

  const RenderIndicators = () => {
    return (
      <Row gutter={[16, 16]}>
        {riskIndicators.map(({ indicatorKey, indicatorValue }, index) => {
          return (
            <Col
              className="gutter-row"
              span={8}
              key={index}
              onMouseEnter={() => setShowFullValue(index)}
              onMouseLeave={() => setShowFullValue('')}>
              <div className={style.indicator}>
                <span className={style.label}>{indicatorKey}</span>
                <span className={style.value}>
                  {showFullValue === index ||
                  Number.isNaN(indicatorValue) ||
                  indicatorValue <= 0
                    ? indicatorValue
                    : getInDecimal(indicatorValue)}
                </span>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  const getHeaderTitle = () => {
    return (
      <>
        Cluster Risk Indicators
        {quadrant?.quadrantName
          ? ` [${quadrant?.quadrantName}-${quadrant?.meanScore}%-${quadrant?.riskScore}] `
          : ' '}
        <Popover
          placement="top"
          content={
            <div style={{ padding: 10 }}>
              <span>
                Cluster name is defined as - {'<'}Quadrant-Alert yield%-Risk
                score{'>'}
              </span>
            </div>
          }>
          {quadrant?.quadrantName && (
            <span className={style.popoverIcon}>
              <QuestionCircleTwoTone />
            </span>
          )}
        </Popover>
      </>
    );
  };

  return (
    <Card header={getHeaderTitle()}>
      <Tabs activeKey={activeTabKey} onChange={onTabChange}>
        {CRS_CLUSTER_RISK_INDICATOR_TAB.map((tab, index) => (
          <TabPane
            tab={<span className={style.tabName}>{tab.toLowerCase()}</span>}
            disabled={quadrant?.quadrantName ? false : true}
            key={`${index + 1}`}>
            <Spin spinning={isLoading}>
              {riskIndicators.length === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No results found"
                />
              )}
              <RenderIndicators />
              {riskIndicators.length > 0 && (
                <div className={style.pagination}>
                  <Pagination
                    defaultCurrent={defaultPage}
                    defaultPageSize={defaultPageSize}
                    showTotal={(total) => (
                      <span className={style.total}>{total} Records</span>
                    )}
                    total={clusterInformation?.count}
                    onChange={onPaginationChange}
                    current={currentPage}
                  />
                </div>
              )}
            </Spin>
          </TabPane>
        ))}
      </Tabs>
    </Card>
  );
};

ClusterRiskIndicators.propTypes = {
  isLoading: PropTypes.bool,
  clusterInformation: PropTypes.object,
  latestCluster: PropTypes.object.isRequired,
  onPaginationChange: PropTypes.func,
  defaultPage: PropTypes.number,
  defaultPageSize: PropTypes.number,
  currentPage: PropTypes.number,
  tabChange: PropTypes.func,
  activeTabKey: PropTypes.string
};

export default ClusterRiskIndicators;
